import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Form,
  Input,
  Select,
  Switch,
  Alert,
  Tabs,
  Row,
  Col,
  Tag,
  Divider,
  message,
  Spin,
} from 'antd';
import {
  DollarOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  MobileOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { adminAPI, AdConfigItem } from '../services/adminAPI';

const { Title, Text, Paragraph } = Typography;

const SettingsAds: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<AdConfigItem[]>([]);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [globalTestMode, setGlobalTestMode] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAdConfigs();
      if (res.success && res.data) {
        setConfigs(res.data);
        populateForm(res.data);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to fetch ad configurations');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (data: AdConfigItem[]) => {
    const webRows = data.filter((c) => c.platform === 'web' && c.provider === 'adsense');
    const androidRows = data.filter((c) => c.platform === 'android' && c.provider === 'admob');
    const iosRows = data.filter((c) => c.platform === 'ios' && c.provider === 'admob');

    const publisherId = webRows[0]?.appOrClientId || 'ca-pub-5124659385888505';
    const isAnyActive = data.some((c) => c.isActive);
    const isAnyTest = data.some((c) => c.isTestMode);
    setGlobalEnabled(isAnyActive);
    setGlobalTestMode(isAnyTest);

    const a79 = webRows.find((c) => c.placementKey === 'a79');
    const a16 = webRows.find((c) => c.placementKey === 'a16');
    const a32 = webRows.find((c) => c.placementKey === 'a32');

    const androidBanner = androidRows.find((c) => c.placementKey === 'banner');
    const androidInterstitial = androidRows.find((c) => c.placementKey === 'interstitial');
    const androidRewarded = androidRows.find((c) => c.placementKey === 'rewarded');

    const iosBanner = iosRows.find((c) => c.placementKey === 'banner');
    const iosInterstitial = iosRows.find((c) => c.placementKey === 'interstitial');
    const iosRewarded = iosRows.find((c) => c.placementKey === 'rewarded');

    form.setFieldsValue({
      adsensePublisherId: publisherId,
      webActive: webRows.some((c) => c.isActive),
      webTestMode: webRows.some((c) => c.isTestMode),
      a79Slot: a79?.slotOrUnitId || '6268962937',
      a79Active: a79 ? a79.isActive : true,
      a16Slot: a16?.slotOrUnitId || '6268962937',
      a16Active: a16 ? a16.isActive : true,
      a32Slot: a32?.slotOrUnitId || '6268962937',
      a32Active: a32 ? a32.isActive : true,

      // Android AdMob
      androidAppId: androidRows[0]?.appOrClientId || 'ca-app-pub-2328505132374624~9936465036',
      androidActive: androidRows.some((c) => c.isActive),
      androidBannerSlot: androidBanner?.slotOrUnitId || 'ca-app-pub-2328505132374624/7338221856',
      androidInterstitialSlot: androidInterstitial?.slotOrUnitId || 'ca-app-pub-2328505132374624/6328392519',
      androidRewardedSlot: androidRewarded?.slotOrUnitId || 'ca-app-pub-2328505132374624/3319085795',

      // iOS AdMob
      iosAppId: iosRows[0]?.appOrClientId || 'ca-app-pub-2328505132374624~9936465036',
      iosActive: iosRows.some((c) => c.isActive),
      iosBannerSlot: iosBanner?.slotOrUnitId || 'ca-app-pub-2328505132374624/7338221856',
      iosInterstitialSlot: iosInterstitial?.slotOrUnitId || 'ca-app-pub-2328505132374624/6328392519',
      iosRewardedSlot: iosRewarded?.slotOrUnitId || 'ca-app-pub-2328505132374624/3319085795',
    });
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const allFields = form.getFieldsValue(true);
      const merged = { ...allFields, ...values };

      const getExisting = (platform: string, provider: string, placementKey: string) => {
        return configs.find(
          (c) => c.platform === platform && c.provider === provider && c.placementKey === placementKey
        );
      };

      const adsenseId =
        merged.adsensePublisherId?.trim() ||
        getExisting('web', 'adsense', 'a79')?.appOrClientId ||
        'ca-pub-5124659385888505';
      const a79Slot = merged.a79Slot?.trim() || getExisting('web', 'adsense', 'a79')?.slotOrUnitId || '6268962937';
      const a16Slot = merged.a16Slot?.trim() || getExisting('web', 'adsense', 'a16')?.slotOrUnitId || '6268962937';
      const a32Slot = merged.a32Slot?.trim() || getExisting('web', 'adsense', 'a32')?.slotOrUnitId || '6268962937';

      const androidAppId =
        merged.androidAppId?.trim() ||
        getExisting('android', 'admob', 'banner')?.appOrClientId ||
        'ca-app-pub-2328505132374624~9936465036';
      const androidBanner =
        merged.androidBannerSlot?.trim() ||
        getExisting('android', 'admob', 'banner')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/7338221856';
      const androidInterstitial =
        merged.androidInterstitialSlot?.trim() ||
        getExisting('android', 'admob', 'interstitial')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/6328392519';
      const androidRewarded =
        merged.androidRewardedSlot?.trim() ||
        getExisting('android', 'admob', 'rewarded')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/3319085795';

      const iosAppId =
        merged.iosAppId?.trim() ||
        getExisting('ios', 'admob', 'banner')?.appOrClientId ||
        'ca-app-pub-2328505132374624~9936465036';
      const iosBanner =
        merged.iosBannerSlot?.trim() ||
        getExisting('ios', 'admob', 'banner')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/7338221856';
      const iosInterstitial =
        merged.iosInterstitialSlot?.trim() ||
        getExisting('ios', 'admob', 'interstitial')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/6328392519';
      const iosRewarded =
        merged.iosRewardedSlot?.trim() ||
        getExisting('ios', 'admob', 'rewarded')?.slotOrUnitId ||
        'ca-app-pub-2328505132374624/3319085795';

      const payloadConfigs = [
        // Web AdSense slots
        {
          platform: 'web',
          provider: 'adsense',
          placementKey: 'a79',
          appOrClientId: adsenseId,
          slotOrUnitId: a79Slot,
          format: 'horizontal',
          isActive: globalEnabled && (merged.webActive ?? true) && (merged.a79Active ?? true),
          isTestMode: globalTestMode || (merged.webTestMode ?? false),
        },
        {
          platform: 'web',
          provider: 'adsense',
          placementKey: 'a16',
          appOrClientId: adsenseId,
          slotOrUnitId: a16Slot,
          format: 'vertical',
          isActive: globalEnabled && (merged.webActive ?? true) && (merged.a16Active ?? true),
          isTestMode: globalTestMode || (merged.webTestMode ?? false),
        },
        {
          platform: 'web',
          provider: 'adsense',
          placementKey: 'a32',
          appOrClientId: adsenseId,
          slotOrUnitId: a32Slot,
          format: 'rectangle',
          isActive: globalEnabled && (merged.webActive ?? true) && (merged.a32Active ?? true),
          isTestMode: globalTestMode || (merged.webTestMode ?? false),
        },
        // Android AdMob
        {
          platform: 'android',
          provider: 'admob',
          placementKey: 'banner',
          appOrClientId: androidAppId,
          slotOrUnitId: androidBanner,
          format: 'banner',
          isActive: globalEnabled && (merged.androidActive ?? false),
          isTestMode: globalTestMode,
        },
        {
          platform: 'android',
          provider: 'admob',
          placementKey: 'interstitial',
          appOrClientId: androidAppId,
          slotOrUnitId: androidInterstitial,
          format: 'interstitial',
          isActive: globalEnabled && (merged.androidActive ?? false),
          isTestMode: globalTestMode,
        },
        {
          platform: 'android',
          provider: 'admob',
          placementKey: 'rewarded',
          appOrClientId: androidAppId,
          slotOrUnitId: androidRewarded,
          format: 'rewarded',
          isActive: globalEnabled && (merged.androidActive ?? false),
          isTestMode: globalTestMode,
        },
        // iOS AdMob
        {
          platform: 'ios',
          provider: 'admob',
          placementKey: 'banner',
          appOrClientId: iosAppId,
          slotOrUnitId: iosBanner,
          format: 'banner',
          isActive: globalEnabled && (merged.iosActive ?? false),
          isTestMode: globalTestMode,
        },
        {
          platform: 'ios',
          provider: 'admob',
          placementKey: 'interstitial',
          appOrClientId: iosAppId,
          slotOrUnitId: iosInterstitial,
          format: 'interstitial',
          isActive: globalEnabled && (merged.iosActive ?? false),
          isTestMode: globalTestMode,
        },
        {
          platform: 'ios',
          provider: 'admob',
          placementKey: 'rewarded',
          appOrClientId: iosAppId,
          slotOrUnitId: iosRewarded,
          format: 'rewarded',
          isActive: globalEnabled && (merged.iosActive ?? false),
          isTestMode: globalTestMode,
        },
      ];

      const res = await adminAPI.bulkUpdateAdConfigs({
        configs: payloadConfigs,
        globalEnabled,
        globalTestMode,
      });

      if (res.success) {
        message.success('Ad configurations saved successfully! Changes are live immediately.');
        await fetchConfigs();
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save ad configurations');
    } finally {
      setSaving(false);
    }
  };

  const tabItems = [
    {
      key: 'web',
      forceRender: true,
      label: (
        <span>
          <GlobalOutlined /> Web Ads (Google AdSense)
        </span>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Google AdSense Web Integration"
            description="Changes to the Publisher ID and Slot IDs will dynamically take effect on the web platform without requiring a code rebuild or redeployment."
            type="info"
            showIcon
          />

          <Card title="AdSense Account Settings" size="small">
            <Row gutter={24}>
              <Col xs={24} md={16}>
                <Form.Item
                  label="AdSense Publisher ID"
                  name="adsensePublisherId"
                  tooltip="Found in your Google AdSense account (e.g. ca-pub-5124659385888505)"
                  rules={[{ required: true, message: 'Please enter your AdSense Publisher ID' }]}
                >
                  <Input placeholder="ca-pub-5124659385888505" size="large" />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label="Web Ads Active" name="webActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label="AdSense Test Mode" name="webTestMode" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Web Ad Slot Placements (Grid Detail Page)" size="small">
            <Row gutter={16}>
              {/* Slot a79 */}
              <Col xs={24} md={8}>
                <Card type="inner" title="Slot a79 (Horizontal Banner)" extra={<Tag color="blue">Row Banner</Tag>}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Appears across columns 5–17 below the game area.
                  </Text>
                  <Form.Item label="Ad Slot ID" name="a79Slot">
                    <Input placeholder="6268962937" />
                  </Form.Item>
                  <Form.Item label="Enabled" name="a79Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Card>
              </Col>

              {/* Slot a16 */}
              <Col xs={24} md={8}>
                <Card type="inner" title="Slot a16 (Vertical Skyscraper)" extra={<Tag color="green">Tower</Tag>}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Vertical tower appearing in columns 8–10 and 16–17.
                  </Text>
                  <Form.Item label="Ad Slot ID" name="a16Slot">
                    <Input placeholder="6268962937" />
                  </Form.Item>
                  <Form.Item label="Enabled" name="a16Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Card>
              </Col>

              {/* Slot a32 */}
              <Col xs={24} md={8}>
                <Card type="inner" title="Slot a32 (Medium Rectangle)" extra={<Tag color="purple">Box</Tag>}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Rectangle unit appearing in columns 11–17.
                  </Text>
                  <Form.Item label="Ad Slot ID" name="a32Slot">
                    <Input placeholder="6268962937" />
                  </Form.Item>
                  <Form.Item label="Enabled" name="a32Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Card>
              </Col>
            </Row>
          </Card>
        </Space>
      ),
    },
    {
      key: 'mobile',
      forceRender: true,
      label: (
        <span>
          <MobileOutlined /> Mobile Ads (Google AdMob)
        </span>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Capacitor Mobile AdMob Integration"
            description="Configure native mobile ad IDs. Native apps receive these updates over the air without requiring App Store / Google Play submissions."
            type="info"
            showIcon
          />

          <Row gutter={24}>
            {/* Android Settings */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <MobileOutlined style={{ marginRight: 8 }} /> Android AdMob
                  </span>
                }
                size="small"
              >
                <Form.Item label="Android Ads Enabled" name="androidActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Off" />
                </Form.Item>

                <Form.Item
                  label="Android AdMob App ID"
                  name="androidAppId"
                  tooltip="AdMob Application ID for Android"
                >
                  <Input placeholder="ca-app-pub-xxx~xxx" />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }} />

                <Form.Item label="Banner Ad Unit ID" name="androidBannerSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>

                <Form.Item label="Interstitial Ad Unit ID" name="androidInterstitialSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>

                <Form.Item label="Rewarded Video Unit ID" name="androidRewardedSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>
              </Card>
            </Col>

            {/* iOS Settings */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <MobileOutlined style={{ marginRight: 8 }} /> iOS AdMob
                  </span>
                }
                size="small"
              >
                <Form.Item label="iOS Ads Enabled" name="iosActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Off" />
                </Form.Item>

                <Form.Item
                  label="iOS AdMob App ID"
                  name="iosAppId"
                  tooltip="AdMob Application ID for iOS"
                >
                  <Input placeholder="ca-app-pub-xxx~xxx" />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }} />

                <Form.Item label="Banner Ad Unit ID" name="iosBannerSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>

                <Form.Item label="Interstitial Ad Unit ID" name="iosInterstitialSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>

                <Form.Item label="Rewarded Video Unit ID" name="iosRewardedSlot">
                  <Input placeholder="ca-app-pub-xxx/xxx" />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Space>
      ),
    },
    {
      key: 'mediation',
      label: (
        <span>
          <ApiOutlined /> Future Networks & Mediation
        </span>
      ),
      children: (
        <Card size="small">
          <Paragraph>
            The backend database structure is built on a generic <code>(platform, provider, placementKey)</code> model.
            This architecture enables plugging in additional monetization providers in the future:
          </Paragraph>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Card size="small" type="inner" title="AppLovin MAX / Unity">
                <Tag color="orange">Mobile Mediation</Tag>
                <Paragraph style={{ marginTop: 8 }}>
                  Support mediation networks alongside AdMob for bidding optimization and higher eCPM.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" type="inner" title="Direct Sponsorships">
                <Tag color="cyan">Direct Deals</Tag>
                <Paragraph style={{ marginTop: 8 }}>
                  Serve direct sponsor banners into slots without intermediary network rev-share.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" type="inner" title="House Ads / Self-Promo">
                <Tag color="purple">Fallback</Tag>
                <Paragraph style={{ marginTop: 8 }}>
                  Promote your own featured games or premium memberships when ad networks fill empty.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <DollarOutlined style={{ marginRight: 8, color: '#52c41a' }} />
            Ads & Monetization Manager
          </Title>
          <Text type="secondary">
            Dynamically configure publisher IDs, slot placements, and mobile networks without code redeployment.
          </Text>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchConfigs} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            loading={saving}
            onClick={() => form.submit()}
          >
            Save All Changes
          </Button>
        </Space>
      </div>

      {/* Master Controls Card */}
      <Card
        style={{ marginBottom: 24, borderLeft: '4px solid #1890ff' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row gutter={24} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space align="center">
              <Switch
                checked={globalEnabled}
                onChange={setGlobalEnabled}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
              <div>
                <Text strong>Global Ads Master Switch</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {globalEnabled ? 'Ads are enabled across all platforms' : 'All ads disabled (Kill Switch active)'}
                </Text>
              </div>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Space align="center">
              <Switch
                checked={globalTestMode}
                onChange={setGlobalTestMode}
                checkedChildren="TEST"
                unCheckedChildren="LIVE"
              />
              <div>
                <Text strong>Global Test Mode</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {globalTestMode ? 'Test ads served (data-adtest="on")' : 'Live production ads'}
                </Text>
              </div>
            </Space>
          </Col>

          <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
            <Tag color={globalEnabled ? 'success' : 'error'} style={{ fontSize: 14, padding: '4px 10px' }}>
              {globalEnabled ? 'System Status: Active' : 'System Status: Ads Paused'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {/* Main Configuration Tabs */}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" preserve={true} onFinish={handleSave}>
          <Tabs defaultActiveKey="web" items={tabItems} size="large" />
        </Form>
      </Spin>
    </div>
  );
};

export default SettingsAds;
